<script lang="ts" setup>
  import { CATEGORIES, DATA_LIST_ITEM, DATA_LIST_ITEM_WITH_BRACKETS } from '@/utils/constants/origDest'
  import type { IOrigin, TDestin, TFilteredOrigDestResult, TCategories } from '@/types/OrigDest'
  import { removeHtmlTags } from '@/utils/stringManipulation'

  const modelValue = defineModel<IOrigin | TDestin | null>()
  const isSubCategoriesDisabled = defineModel<boolean>('isSubCategoriesDisabled', { default: false })
  const searchTerm = defineModel<string>('searchTerm', { default: '' })
  const isOpen = defineModel<boolean>('isOpen', { default: false })

  const props = defineProps<{
    data: TFilteredOrigDestResult[]
    category: TCategories
    subCategory: string
  }>()

  const emit = defineEmits<{
    selected: []
  }>()

  const { data, category, subCategory } = toRefs(props)

  const { list, containerProps, wrapperProps, scrollTo } = useVirtualList(data, {
    itemHeight: 68,
    overscan: 10
  })

  const canFocusListItem = ref(true)

  const isEmptyDataList = computed(() => {
    return data.value?.length <= 1
  })

  const isEmptySearchDataList = computed(() => {
    return isEmptyDataList.value && !!searchTerm.value
  })

  const handleKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Tab' && event.shiftKey) {
      isSubCategoriesDisabled.value = false
    }
  }

  const moveFocus = (direction: 1 | -1) => {
    const currentElement = document.activeElement as HTMLElement
    if (!currentElement || !currentElement.hasAttribute(DATA_LIST_ITEM)) return

    const targetElement = getListItemSibling(currentElement, direction)

    if (targetElement) {
      targetElement.focus()
    }
  }
  const getListItemSibling = (element: HTMLElement, direction: 1 | -1) => {
    const siblingDirection = direction === 1 ? 'nextElementSibling' : 'previousElementSibling'
    let siblingElement = element.parentElement?.[siblingDirection] as HTMLElement

    while (siblingElement) {
      if (siblingElement?.querySelector(DATA_LIST_ITEM_WITH_BRACKETS)) {
        return siblingElement.querySelector(DATA_LIST_ITEM_WITH_BRACKETS) as HTMLElement
      }
      siblingElement = siblingElement?.[siblingDirection] as HTMLElement
    }

    return null
  }

  const clearSearchResultTags = (item: IOrigin | TDestin) => {
    item.apt = removeHtmlTags(item.apt)
    item.city = removeHtmlTags(item.city)
    item.code = removeHtmlTags(item.code)
    item.country = removeHtmlTags(item.country)

    return item
  }
  const selectOrigDest = (item: IOrigin | TDestin) => {
    if (searchTerm.value) {
      clearSearchResultTags(item)
    }
    modelValue.value = item
    emit('selected')
    isOpen.value = false
    searchTerm.value = ''
  }

  const listItems = ref<HTMLElement[] | null>()
  const container = ref<HTMLElement | null>()
  const focusIndex = ref(1)
  const getListIndexFocus = () => {
    if (!listItems.value?.length || !listItems.value || !container.value) {
      focusIndex.value = 1

      return
    }

    const containerRect = container.value.getBoundingClientRect()

    const firstVisibleItem = listItems.value.find((item) => {
      const itemRect = item.getBoundingClientRect()

      return itemRect.top >= containerRect.top && itemRect.top < containerRect.bottom
    })

    if (firstVisibleItem) {
      const dataIndex = firstVisibleItem.getAttribute(DATA_LIST_ITEM)
      focusIndex.value = dataIndex ? parseInt(dataIndex, 10) : 2
    } else {
      focusIndex.value = 1
    }
  }

  watch([searchTerm, category, subCategory], () => {
    nextTick(() => {
      scrollTo(0)
    })
  })

  watchDebounced(
    [list, category],
    () => {
      listItems.value = Array.from(document.querySelectorAll(DATA_LIST_ITEM_WITH_BRACKETS)) || []
      container.value = (document.querySelector('[data-container]') as HTMLElement) || null
      getListIndexFocus()
    },
    { debounce: 500, maxWait: 1000 }
  )
</script>

<template>
  <div
    v-bind="containerProps"
    data-container
    class="h-[calc(100svh-9rem)] w-full overflow-y-auto md:h-[40vh] md:max-h-90"
    :class="{ 'text-secondary md:h-auto': isEmptySearchDataList }"
    role="listbox"
    tabindex="-1"
    @keydown="handleKeydown"
    @keydown.prevent.down="moveFocus(1)"
    @keydown.prevent.up="moveFocus(-1)"
  >
    <div v-bind="wrapperProps">
      <div
        v-for="(item, index) in list"
        :key="item.index"
        class="items-left mx-4 my-2 flex min-h-6"
        :class="{ '!min-h-14': isEmptySearchDataList }"
      >
        <span
          v-if="'title' in item.data"
          class="flex items-center text-subtitle-1"
        >
          {{ item.data.title }}
          <span
            v-if="isEmptySearchDataList"
            class="ml-1 text-bt-blue-dark"
          >
            {{ searchTerm }}
          </span>
        </span>
        <div
          v-else-if="item.data.apt"
          :data-list-index="index"
          :class="{ 'bg-divider': modelValue?.code === item.data.code }"
          :tabindex="canFocusListItem && index === focusIndex ? 0 : -1"
          role="option"
          :aria-selected="modelValue?.code === item.data.code"
          class="flex w-full cursor-pointer justify-between rounded px-4 py-2.5 hover:bg-divider"
          @click="selectOrigDest(item.data)"
          @keydown.prevent.enter.stop="selectOrigDest(item.data)"
          @focus="canFocusListItem = false"
          @blur="canFocusListItem = true"
        >
          <div
            :class="{ 'w-2/3': item.data?.hasBTDirect }"
            class="flex flex-col justify-between gap-1 text-subtitle-1"
          >
            <span v-html="$t('origDest.aptCode', { apt: item.data.apt, code: item.data.code })" />
            <span class="text-grey-secondary text-body-2">
              <span v-html="$t('origDest.city', { city: item.data.city })" />
              <span
                v-if="category !== CATEGORIES.COUNTRIES || searchTerm"
                v-html="`, ${$t('origDest.country', { country: item.data.country })}`"
              />
            </span>
          </div>
          <div
            v-if="item.data?.hasBTDirect"
            class="flex w-1/3 items-center justify-end gap-1 md:gap-2"
          >
            <span class="text-xs text-grey-secondary">{{ $t('origDest.directFlight') }}</span>
            <Icon
              name="common/round-airplane-90-deg"
              filled
              class="text-bt-dark-blue"
              size="sm"
            />
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="isEmptyDataList && !searchTerm"
      class="mt-32 flex justify-center"
    >
      <div class="text-secondary">{{ $t(`origDest.notSelected.${category}`) }}</div>
    </div>
  </div>
</template>
